/**
 * API client — fetch wrapper.
 * STANDAR Section 11: relative path /api/..., credentials: 'omit'.
 * Auth: Authorization Bearer (Portal JWT) — token-storage.ts
 */

import { getStoredToken, redirectToLogin } from '../utils/token-storage';

export interface ApiError {
  ok: false;
  error: string;
  message?: string;
}

export interface ApiResult<T> {
  ok: true;
  data: T;
}

async function parseResponseBody(res: Response): Promise<any> {
  const text = await res.text();
  if (!text) return {};

  const ct = (res.headers.get('content-type') || '').toLowerCase();
  const looksJson = ct.includes('application/json') || text.trimStart().startsWith('{') || text.trimStart().startsWith('[');

  if (looksJson) {
    try {
      return JSON.parse(text);
    } catch {
      // fall through
    }
  }

  // Nginx 502/504 HTML, plain text, dll
  const snippet = text.replace(/\s+/g, ' ').trim().slice(0, 120);
  throw new Error(
    res.status === 504
      ? 'Gateway timeout (504) — coba lagi sebentar'
      : res.status === 502
        ? 'Upstream unreachable (502)'
        : `HTTP ${res.status}: respons non-JSON${snippet ? ` — ${snippet}` : ''}`,
  );
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getStoredToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Bot run bisa 1–2 menit — browser default cukup; AbortSignal optional
  const res = await fetch(path, {
    ...options,
    headers,
    credentials: 'omit', // STANDAR: no cookie
  });

  // 401 → redirect ke Portal SSO
  if (res.status === 401) {
    localStorage.removeItem('app_access_token');
    redirectToLogin(window.location.pathname);
    throw new Error('unauthorized');
  }

  const body = await parseResponseBody(res);

  if (!res.ok || body.ok === false) {
    throw new Error(body.message || body.error || `HTTP ${res.status}`);
  }

  return body;
}

export const api = {
  get<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'GET' });
  },

  post<T>(path: string, body?: any): Promise<T> {
    return request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  put<T>(path: string, body?: any): Promise<T> {
    return request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'DELETE' });
  },
};

// === API response types ===
export interface GateResults {
  macro: {
    passed: boolean;
    settle_date: string;
    buffer_hours: number;
    nearest_event: { name: string; date: string; diff_hours: number } | null;
  };
  liquidation_zone: {
    passed: boolean;
    tolerance_pct: number;
    min_intensity: number;
    strike_zone_from: number;
    strike_zone_to: number;
    nearest_cluster: { price: number; intensity: number; side: string } | null;
  };
  min_distance: {
    passed: boolean;
    distance: number;
    atr_14d: number;
    multiplier: number;
    required: number;
  };
}

export interface DcdProduct {
  strikePrice: string | number;
  optionType: 'PUT' | 'CALL';
  apr: string | number;
  settleDate?: number;
  duration?: number;
}

/** Produk DCD lengkap dari tc-binance /dcd/list (normalized) */
export interface DcdExplorerProduct {
  id: string;
  productId: string;
  underlying: string;
  strikePrice: number;
  optionType: 'PUT' | 'CALL';
  apr: number;
  settleDate: string;
  settlementDate: string;
  autoCompoundPlan: string;
  investCoin: string;
  exercisedCoin: string;
  duration: number;
  minAmount: number;
  maxAmount: number;
  raw: Record<string, any>;
}

/** Respon dari GET /api/binance/dcd/list */
export interface DcdListResponse {
  ok: true;
  data: {
    underlying: string;
    products: DcdExplorerProduct[];
    count: number;
    errors?: string[];
  };
}

/** Respon dari GET /api/binance/price */
export interface PriceResponse {
  ok: true;
  data: {
    symbol: string;
    price: number;
  };
}

export function getOptionLabel(optionType: 'PUT' | 'CALL'): string {
  return optionType === 'PUT' ? 'Buy Low USDT' : 'Sell High BTC';
}

export function getOptionIcon(optionType: 'PUT' | 'CALL'): string {
  return optionType === 'PUT' ? '↓' : '↑';
}

export interface Candidate {
  id: number;
  run_at: string;
  cycle_id: string;
  underlying: string;
  strike_price: number;
  option_type: 'PUT' | 'CALL';
  settle_date: string;
  days_to_settle: number;
  apr: number;
  soft_score: number;
  gate_passed: { macro: boolean; liquidation_zone: boolean; min_distance: boolean };
  gate_results?: GateResults;
  reject_reason: string | null;
  narrative_id?: number;
  narrative?: string;
}

export interface CycleSummary {
  cycle_id: string;
  total: number;
  passed: number;
  rejected: number;
  gate1_fail: number;
  gate2_fail: number;
  gate3_fail: number;
  soft_fail: number;
  avg_score: number;
  min_score: number;
  max_score: number;
}

export interface Position {
  id: number;
  position_id: string;
  underlying: string;
  strike_price: number;
  option_type: string;
  apr: number;
  invested: number;
  settle_date: string;
  status: string;
  subscribed_at: string;
  settled_at: string | null;
  settlement_pnl: number | null;
  settle_asset: string | null;
}

export interface MarketContext {
  symbol: string;
  spot_price: number;
  dvol: number;
  fear_greed: number;
  funding_rate: number;
  open_interest: number;
  max_pain: number;
  macro_events?: Array<{ date: string; impact: string; name: string }>;
  raw?: Record<string, any>;
}
